<script lang="ts">
	import type { EmblaCarouselType } from "embla-carousel";
	import Autoplay from "embla-carousel-autoplay";
	import type { Category } from "@core/schema/Category.js";
	import type { Media as MediaType } from "@core/schema/Media.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./../templates/default.translations.json" with { type: "json" };
	import CarouselArrows from "./CarouselArrows.svelte";
	import Carousel from "./common/Carousel.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Media from "./common/Media.svelte";

	const translate = getTranslate(translations);

	const {
		references,
		bgColor = "",
	}: {
		references: Category["references"];
		bgColor?: string;
	} = $props();

	let carouselApi: EmblaCarouselType | undefined = $state();

	// eslint-disable-next-line new-cap
	const autoplayPlugin = Autoplay({
		delay: 8000,
		stopOnInteraction: false,
		stopOnMouseEnter: true,
		stopOnLastSnap: false,
	});
</script>

{#if references?.photos}
	<section class="full-width {bgColor} py-8 sm:py-12 md:py-16 lg:py-20">
		<div class="container-center grid-container">
			<div class="flex flex-col justify-between gap-6 sm:gap-8 mb-8 md:mb-12">
				{#if references.title}
					<h2 class="text-left">
						<Formatted text={references.title} />
					</h2>
				{/if}
				{#if references.description}
					<p class="font-normal text-left lg:w-[70%] lg:text-lg/[1.4] text-optical-black">
						<Formatted text={references.description} />
					</p>
				{/if}
			</div>
		</div>

		<div class="col-span-full flex flex-col gap-6">
			<Carousel
				items={references.photos}
				plugins={[autoplayPlugin]}
				options={{
					loop: true,
				}}
				bind:api={carouselApi}
			>
				{#snippet item(photo: MediaType, index: number)}
					{@const originalIndex = index % references.photos.length}
					<!-- svelte-ignore a11y_no_static_element_interactions -->
					<!-- svelte-ignore a11y_click_events_have_key_events -->
					<div
						class="basis-[80%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4 shrink-0 grow-0 mr-4 group cursor-pointer outline-none"
						onclick={(e: MouseEvent): void => {
							const link = (e.currentTarget as HTMLElement).querySelector<HTMLElement>("a[data-fslightbox]");
							link?.click();
						}}
					>
						<div class="relative w-full h-96 md:h-[28rem] lg:h-[32rem] rounded-2xl overflow-hidden">
							<Media
								class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
								source={photo}
								wrapperClass="absolute inset-0"
								width={800}
								height={720}
								alt={photo.title ?? `${translate("references")} ${originalIndex + 1}`}
								lightbox="references"
								lightboxWidth={1600}
							/>
							<div
								class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none"
							></div>
							<div class="hidden sm:block absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8 pointer-events-none">
								<h3 class="text-white text-sm sm:text-base lg:text-lg font-semibold drop-shadow-2xl leading-tight">
									<Formatted text={photo.title} />
								</h3>
							</div>
						</div>
						<p class="sm:hidden mt-3 text-sm text-optical-black font-medium leading-snug px-1">
							<Formatted text={photo.title} />
						</p>
					</div>
				{/snippet}
			</Carousel>

			<div class="sm:flex hidden flex-col items-center">
				<div class="container-center">
					<CarouselArrows api={carouselApi} slideCount={references.photos.length} />
				</div>
			</div>
		</div>
	</section>
{/if}
